import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import {
    Box, Button, Paper, Stack, Typography, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Checkbox, FormControlLabel, Tooltip, IconButton, Chip, Switch,
} from '@mui/material';
import { CheckCircle, Edit } from "@mui/icons-material";
import axiosInstance from '../utils/axiosInstance';
import TableView from '../components/TableView';
import InputFieldAddress from '../components/InputFieldAddress';
const WarehousesPage = ({ getLimit }) => {
    const { showSnackbar } = useSnackbar();
    const [error, setError] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const { register, control, handleSubmit, reset, formState: { errors }, setValue } = useForm({
        defaultValues: {
            warehouse_name: '',
            postal_code: '', state: '', address_line: '',
            is_default: false
        }
    });

    const loadWarehouses = async () => {
        try {
            const res = await axiosInstance.get('/warehouse');
            setWarehouses(res.data ?? []);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            setError(err?.response?.data?.message ?? 'データの取得に失敗しました');
        }
    };

    useEffect(() => {
        loadWarehouses();
    }, []);

    const openNew = () => {
        setEditing(null);
        reset({ warehouse_name: '', postal_code: '', state: '', address_line: '', is_default: false, status: true });
        setOpen(true);
    };

    const openEdit = (w) => {
        if (!w) return;
        setEditing(w);
        reset({
            warehouse_name: w.warehouse_name ?? '',
            postal_code: w.postal_code ?? '',
            state: w.state ?? '',
            address_line: w.address_line ?? '',
            is_default: w.is_default ?? false,
            status: w.status ?? true,
        });
        setOpen(true);
    };

    const onSubmit = async (data) => {
        if (saving) return;
        setSaving(true);
        try {
            const payload = {
                ...data,
                warehouse_name: data.warehouse_name ?? '',
                postal_code: data.postal_code ?? '',
                state: data.state ?? '',
                address_line: data.address_line ?? '',
                is_default: data.is_default ?? false,
                status: data.status ?? true,
            };

            if (editing) {
                await axiosInstance.put(`/warehouse/${editing.warehouse_id}`, payload);
            } else {
                await axiosInstance.post('/warehouse', payload);
            }

            setOpen(false);
            await loadWarehouses();
            showSnackbar('保存しました', 'success');
        } catch (err) {
            if (process.env.NODE_ENV === 'development') console.error(err);
            showSnackbar(err?.response?.data?.message ?? '保存に失敗しました', 'error');
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            label: "倉庫名",
            accessor: "warehouse_name",
        },
        {
            label: "所在地",
            accessor: "warehouse_location",
            render: (row) => (
                <Typography>〒{row.postal_code || ''} {row.state || ''}{row.address_line || ''}</Typography>
            ),
        },
        {
            label: "デフォルト",
            accessor: "is_default",
            format: (value) =>
                value ? (
                    <CheckCircle fontSize="small" color='primary' />
                ) : null,
        },
        {
            label: "ステータス",
            accessor: "status",
            format: (value) => (
                <Chip
                    label={value ? "有効" : "無効"}
                    color={value ? "success" : "default"}
                    size="small"
                />
            ),
            width: '120px',
        },
        {
            label: "",
            accessor: "actions",
            render: (row) => (
                <Tooltip title="編集">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                        <Edit />
                    </IconButton>
                </Tooltip>
            ),
            width: '50px',
        },
    ];
    const maxWarehouses = getLimit('max_warehouses') ?? 0;
    if (error) return <GlobalError errorMessage={error} />;
    return (
        <>
            <Box
                spacing={1}
                direction="row"
                alignItems="end"
                justifyContent="space-between"
                sx={{ display: 'flex' }}
            >
                <Typography variant="h1">倉庫管理</Typography>
                <Button variant="contained" onClick={openNew}
                    disabled={maxWarehouses <= warehouses.length}
                    sx={{ mb: 1 }}>
                    倉庫を追加
                </Button>
            </Box>
            <Paper variant='section'>
                {warehouses.length >= maxWarehouses && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        倉庫の追加は営業担当にお問合わせください
                    </Alert>
                )}
                <TableView dataset={warehouses} columns={columns} keyColumn="warehouse_id" />
            </Paper>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? '倉庫を編集' : '倉庫を追加'}</DialogTitle>

                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <Controller
                            name="warehouse_name"
                            control={control}
                            rules={{
                                required: '倉庫名は必須です',
                                maxLength: { value: 255, message: '255文字以内で入力してください' },
                            }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    label="倉庫名"
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                        <InputFieldAddress
                            register={register}
                            errors={errors}
                            isEditMode={true}
                            setValue={setValue}
                            control={control}
                            label='配送先住所'
                        />
                        <Controller
                            name="is_default"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value}
                                            onChange={e => field.onChange(e.target.checked)}
                                        />
                                    }
                                    label="この倉庫をデフォルトにする"
                                />
                            )}
                        />
                        {editing && (
                            <Controller
                                name="status"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={field.value}
                                                onChange={e => field.onChange(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={field.value ? "有効" : "無効"}
                                    />
                                )}
                            />
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button variant='outlined' onClick={() => setOpen(false)}>キャンセル</Button>
                    <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={saving}>
                        保存
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
export default WarehousesPage;